// FeedbackCapture — the Signal Engine capture widget (web).
// A friction ladder, voice-first but never voice-only: tap/scale/choice →
// type → 🎙 voice. Renders whatever the (admin-authored) campaign asks for.
// Reuses the MediaRecorder pattern from AIHelpPanel. See docs/feedback_engine_design.md.

import React, { useState } from 'react';
import feedbackAPI, { FieldDef, SignalCampaign, SubmitResult } from '../../services/feedbackAPI';
import VoiceRecorder from './VoiceRecorder';

interface Props {
  campaign: SignalCampaign;
  requestId?: string;
  surface?: string;
  onDone?: () => void;
}

const C = {
  lemon: '#f5b301',
  ink: '#0f172a',
  muted: '#64748b',
  border: '#e2e8f0',
  bgSel: '#fff7e0',
};

function rewardLine(founding?: boolean, newly?: boolean): string {
  // Reflect the REAL grant (server-decided), not the campaign's intent — a tap-only
  // rating never earns Founding Student, only a genuine voice/text contribution does.
  if (founding && newly)
    return '⭐ You’re now a Founding Student! Thank you — you’re helping shape Lomi.';
  if (founding)
    return '⭐ Thank you, Founding Student — this really helps us make Lomi better.';
  return '🍋 Thank you — this really helps us make Lomi better for you.';
}

const FeedbackCapture: React.FC<Props> = ({ campaign, requestId, surface = 'web_inflow', onDone }) => {
  const fields = campaign.fields || [];
  const scaleField = fields.find((f) => f.type === 'scale');

  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [text, setText] = useState('');
  const [showText, setShowText] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recErr, setRecErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [founding, setFounding] = useState<{ founding?: boolean; newly?: boolean }>({});

  const cid = campaign.id || undefined; // open-ended (empty id) → no campaign_id

  const finish = (r?: SubmitResult) => {
    if (r) setFounding({ founding: r.founding_student, newly: r.newly_founding });
    setDone(true);
    setTimeout(() => onDone && onDone(), 2600);
  };

  const setField = (key: string, val: unknown) => setAnswers((a) => ({ ...a, [key]: val }));

  const submitStructured = async (extra?: Record<string, unknown>) => {
    const merged = { ...answers, ...(extra || {}) };
    setBusy(true);
    const r = await feedbackAPI.submit({
      campaign_id: cid,
      request_id: requestId,
      modality: 'structured',
      surface,
      rating: scaleField ? (merged[scaleField.key] as number) ?? null : null,
      answers: merged,
      text: text || null,
    });
    setBusy(false);
    finish(r);
  };

  const submitText = async () => {
    if (!text.trim()) return;
    setBusy(true);
    const r = await feedbackAPI.submit({
      campaign_id: cid,
      request_id: requestId,
      modality: 'text',
      surface,
      text,
    });
    setBusy(false);
    finish(r);
  };

  const submitTap = async (val: number) => {
    setBusy(true);
    const r = await feedbackAPI.submit({
      campaign_id: cid,
      request_id: requestId,
      modality: 'tap',
      surface,
      rating: val,
    });
    setBusy(false);
    finish(r);
  };

  const submitVoiceBlob = async (blob: Blob, seconds: number) => {
    setRecErr('');
    setUploading(true);
    try {
      const r = await feedbackAPI.submitVoice(blob, { campaign_id: cid, request_id: requestId, surface, duration_s: seconds });
      setUploading(false);
      finish(r);
    } catch {
      setUploading(false);
      setRecErr('Upload failed — please try again or type instead.');
    }
  };

  if (done) {
    return <div style={{ color: C.ink, fontSize: 14, padding: '4px 2px' }}>{rewardLine(founding.founding, founding.newly)}</div>;
  }

  const allowVoice = campaign.modalities.includes('voice');
  const allowText = campaign.modalities.includes('text');
  const question = campaign.question_en || campaign.question_am || campaign.name;

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginBottom: 2 }}>{question}</div>
      {campaign.question_en && campaign.question_am && (
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>{campaign.question_am}</div>
      )}

      {/* Structured fields (e.g. a 1–10 scale, choice, boolean) */}
      {fields.map((f) => (
        <FieldInput key={f.key} field={f} value={answers[f.key]} onChange={(v) => setField(f.key, v)} />
      ))}

      {/* Open-ended quick reactions when there are no structured fields */}
      {fields.length === 0 && campaign.modalities.includes('tap') && (
        <div style={{ display: 'flex', gap: 8, margin: '6px 0' }}>
          <button style={chipStyle} disabled={busy} onClick={() => submitTap(1)}>👍 Yes</button>
          <button style={chipStyle} disabled={busy} onClick={() => submitTap(0)}>👎 No</button>
        </div>
      )}

      {/* Voice + text ladder */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
        {allowVoice && !showText && !uploading && (
          <VoiceRecorder
            disabled={busy}
            onComplete={submitVoiceBlob}
            onError={(m) => { setRecErr(m); setShowText(true); }}
          />
        )}
        {allowText && !showText && (
          <button style={chipStyle} disabled={busy} onClick={() => setShowText(true)}>✍️ Type instead</button>
        )}
        {fields.length > 0 && (
          <button
            style={{ ...chipStyle, background: C.lemon, borderColor: C.lemon, color: '#1a1a1a', fontWeight: 700 }}
            disabled={busy || (scaleField && answers[scaleField.key] == null)}
            onClick={() => submitStructured()}
          >
            {busy ? 'Sending…' : 'Submit'}
          </button>
        )}
      </div>

      {allowText && showText && (
        <div style={{ marginTop: 8 }}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your answer…"
            rows={3}
            style={{ width: '100%', borderRadius: 8, border: `1px solid ${C.border}`, padding: 8, fontFamily: 'inherit', fontSize: 14 }}
          />
          <button style={{ ...chipStyle, background: C.lemon, borderColor: C.lemon }} disabled={busy || !text.trim()} onClick={fields.length ? () => submitStructured() : submitText}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        </div>
      )}

      {uploading && <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>Uploading your voice note…</div>}
      {busy && !uploading && <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>Sending…</div>}
      {recErr && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{recErr}</div>}
    </div>
  );
};

const chipStyle: React.CSSProperties = {
  padding: '7px 12px',
  borderRadius: 20,
  border: `1.5px solid ${C.border}`,
  background: '#fff',
  fontSize: 13,
  fontWeight: 600,
  color: C.ink,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const FieldInput: React.FC<{ field: FieldDef; value: unknown; onChange: (v: unknown) => void }> = ({ field, value, onChange }) => {
  const label = field.label_en || field.key;
  if (field.type === 'scale') {
    const min = field.min ?? 1;
    const max = field.max ?? 10;
    const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <div style={{ margin: '8px 0' }}>
        {label && <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{label}</div>}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {nums.map((n) => (
            <button
              key={n}
              onClick={() => onChange(n)}
              style={{
                width: 34, height: 34, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
                border: `1.5px solid ${value === n ? C.lemon : C.border}`,
                background: value === n ? C.lemon : '#fff',
                color: value === n ? '#1a1a1a' : C.ink,
              }}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === 'boolean') {
    return (
      <div style={{ margin: '8px 0', display: 'flex', gap: 8 }}>
        <button style={{ ...chipStyle, background: value === true ? C.bgSel : '#fff' }} onClick={() => onChange(true)}>👍 Yes</button>
        <button style={{ ...chipStyle, background: value === false ? C.bgSel : '#fff' }} onClick={() => onChange(false)}>👎 No</button>
      </div>
    );
  }
  if (field.type === 'choice') {
    return (
      <div style={{ margin: '8px 0' }}>
        {label && <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{label}</div>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(field.options || []).map((opt) => (
            <button key={opt} style={{ ...chipStyle, background: value === opt ? C.bgSel : '#fff', borderColor: value === opt ? C.lemon : C.border }} onClick={() => onChange(opt)}>
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }
  // text field
  return (
    <div style={{ margin: '8px 0' }}>
      {label && <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>{label}</div>}
      <input value={(value as string) || ''} onChange={(e) => onChange(e.target.value)} style={{ width: '100%', borderRadius: 8, border: `1px solid ${C.border}`, padding: 8, fontFamily: 'inherit', fontSize: 14 }} />
    </div>
  );
};

export default FeedbackCapture;
